import { PreferencesHeader } from "@bcc/components/preferences";
import { Button } from "@bcc/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@bcc/components/ui/card";
import { Input } from "@bcc/components/ui/input";
import { Label } from "@bcc/components/ui/label";
import { LoadingIcon } from "@bcc/components/ui/loading-icon/loading-icon";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@bcc/components/ui/select";
import { useInstallationsState } from "@client-shared/providers/installations";
import type { Installation } from "@shared/types";
import { type FormEvent, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  DEFAULT_ORDER_PREFIX,
  ORDER_PREFIX_MAX_LENGTH,
  buildOrderNamePreview,
  buildUpdatedInstallation,
  getInstallationLabel,
  hasOrderConfigChanges,
  normalizeOrderTags,
  pickDefaultInstallationId,
  setOrderConfigurationCardTestId,
  setOrderConfigurationContainerTestId,
  setOrderConfigurationDescriptionTestId,
  setOrderConfigurationEmptyStateTestId,
  setOrderConfigurationInstallationSelectTestId,
  setOrderConfigurationLoadingTestId,
  setOrderConfigurationPreviewTestId,
  setOrderConfigurationSaveButtonTestId,
  setOrderConfigurationTitleTestId,
} from "./order-configuration.utils";
import { OrderTagsInput } from "./order-tags-input";
import { useUpdateOrderConfiguration } from "./use-update-order-configuration";

interface OrderConfigurationFormProps {
  installation: Installation;
}

/**
 * Editable order prefix + order-level tags for a single installation. Keyed by
 * installation id so local edits reset when the roaster switches storefront.
 */
function OrderConfigurationForm({ installation }: OrderConfigurationFormProps) {
  const { t } = useTranslation();
  const { isSaving, saveOrderConfiguration } = useUpdateOrderConfiguration();

  const [orderPrefix, setOrderPrefix] = useState(installation.prefs?.orderPrefix ?? "");
  const [orderTags, setOrderTags] = useState<string[]>(normalizeOrderTags(installation.prefs?.orderTags ?? []));

  const isDirty = hasOrderConfigChanges(installation, orderPrefix, orderTags);
  const canSave = isDirty && !isSaving;

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!canSave) {
        return;
      }
      await saveOrderConfiguration(buildUpdatedInstallation(installation, orderPrefix, orderTags));
    },
    [canSave, installation, orderPrefix, orderTags, saveOrderConfiguration]
  );

  return (
    <form className="space-y-6" onSubmit={handleSubmit}>
      <div className="grid gap-2">
        <Label htmlFor="orderPrefix">{t("views.preferences.orderConfiguration.prefixLabel")}</Label>
        <Input
          id="orderPrefix"
          name="orderPrefix"
          value={orderPrefix}
          maxLength={ORDER_PREFIX_MAX_LENGTH}
          placeholder={t("views.preferences.orderConfiguration.prefixPlaceholder")}
          onChange={(event) => setOrderPrefix(event.target.value)}
        />
        <p className="text-xs text-muted-foreground">
          {t("views.preferences.orderConfiguration.prefixHelper", {
            defaultPrefix: DEFAULT_ORDER_PREFIX,
          })}
        </p>
        <p className="text-xs text-muted-foreground" data-testid={setOrderConfigurationPreviewTestId()}>
          {t("views.preferences.orderConfiguration.preview", {
            orderName: buildOrderNamePreview(orderPrefix),
          })}
        </p>
      </div>

      <div className="grid gap-2">
        <Label>{t("views.preferences.orderConfiguration.tagsLabel")}</Label>
        <OrderTagsInput values={orderTags} onChange={setOrderTags} />
        <p className="text-xs text-muted-foreground">{t("views.preferences.orderConfiguration.tagsHelper")}</p>
      </div>

      <Button type="submit" disabled={!canSave} data-testid={setOrderConfigurationSaveButtonTestId()}>
        {isSaving ? t("views.preferences.orderConfiguration.saving") : t("views.preferences.orderConfiguration.save")}
      </Button>
    </form>
  );
}

/**
 * "Connections" preferences tab — lets a roaster configure the order prefix
 * and order-level tags applied to generated orders. Ports the legacy Beanz
 * Connect installation order settings to BCC.
 *
 * Configuration is stored on `Installation.prefs` and consumed by the order
 * handlers in `shared/src/handlers/orders/*`. A roaster usually has a single
 * storefront; when more than one installation exists a selector is shown.
 */
export function OrderConfiguration() {
  const { t } = useTranslation();

  const [{ initialized, installations }] = useInstallationsState();

  const installationList = useMemo(() => (installations ? Array.from(installations.values()) : []), [installations]);

  const [selectedIdOverride, setSelectedIdOverride] = useState<string | undefined>(undefined);

  // Derive the selected installation during render so it stays valid as the
  // installation list loads or changes, without a set-state-in-effect.
  const selectedId =
    selectedIdOverride && installationList.some((installation) => installation.id === selectedIdOverride)
      ? selectedIdOverride
      : pickDefaultInstallationId(installationList);

  const selectedInstallation = installationList.find((installation) => installation.id === selectedId);

  const renderBody = () => {
    if (!initialized) {
      return (
        <div className="flex justify-center py-8" data-testid={setOrderConfigurationLoadingTestId()}>
          <LoadingIcon />
        </div>
      );
    }

    if (!selectedInstallation) {
      return (
        <p className="text-sm text-muted-foreground" data-testid={setOrderConfigurationEmptyStateTestId()}>
          {t("views.preferences.orderConfiguration.emptyDescription")}
        </p>
      );
    }

    return (
      <div className="space-y-6">
        {installationList.length > 1 && (
          <div className="grid gap-2">
            <Label htmlFor="orderConfigurationInstallation">{t("views.preferences.orderConfiguration.installationLabel")}</Label>
            <Select value={selectedInstallation.id} onValueChange={setSelectedIdOverride}>
              <SelectTrigger
                id="orderConfigurationInstallation"
                className="w-full max-w-md"
                data-testid={setOrderConfigurationInstallationSelectTestId()}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {installationList.map((installation) => (
                  <SelectItem key={installation.id} value={installation.id}>
                    {getInstallationLabel(installation)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <OrderConfigurationForm key={selectedInstallation.id} installation={selectedInstallation} />
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid={setOrderConfigurationContainerTestId()}>
      <PreferencesHeader />

      <Card data-testid={setOrderConfigurationCardTestId()}>
        <CardHeader>
          <CardTitle data-testid={setOrderConfigurationTitleTestId()}>{t("views.preferences.orderConfiguration.title")}</CardTitle>
          <CardDescription data-testid={setOrderConfigurationDescriptionTestId()}>
            {t("views.preferences.orderConfiguration.description")}
          </CardDescription>
        </CardHeader>
        <CardContent>{renderBody()}</CardContent>
      </Card>
    </div>
  );
}
